import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Stack, Typography } from '@mui/material';
import { useSnackbar } from '@/hooks/global-tools';
import Modal, { ModalRef } from '@/components/@IntranetLibrary/Modal';
import Label from '@/components/@IntranetLibrary/Label';
import ReferrerField, {
  type Referrer,
} from '@/components/Approval/@components/ReferrerField';
import { useUserSearch } from '@/components/Approval/@components/ReferrerField/hooks/useUserSearch';
import { addApprovalReferrers } from '@/hooks/approval/useApprovalMentionPermission';
import MessageEditor, { MESSAGE_MAX_LENGTH } from './MessageEditor';
import { SendButton } from './styles';

/** "참조인 추가" 모달 (레거시 apprAddUserModal 포팅) */

type AddReferrerModalProps = {
  open: boolean;
  onClose: () => void;
  documentId: string;
  /** (선택) 추가 성공 후 부모 후처리 — 조회 단건 캐시 무효화(mutate) 등. */
  onAdded?: () => void | Promise<void>;
};

const AddReferrerModal = ({
  open,
  onClose,
  documentId,
  onAdded,
}: AddReferrerModalProps) => {
  const { t } = useTranslation();
  const { showSuccess, showError, showErrorMessage } = useSnackbar();

  const modalRef = useRef<ModalRef>(null);

  const [selected, setSelected] = useState<Referrer[]>([]);
  const [keyword, setKeyword] = useState('');
  const [comboOpen, setComboOpen] = useState(false);
  const [message, setMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // 컨트롤드 open → imperative Modal 동기화. 재오픈 시 폼 초기화.
  useEffect(() => {
    if (open) {
      setSelected([]);
      setKeyword('');
      setComboOpen(false);
      setMessage('');
      modalRef.current?.open();
    } else {
      modalRef.current?.close();
    }
  }, [open]);

  const handleClose = () => {
    if (submitting) return;
    onClose();
  };

  const handleSubmit = async () => {
    if (submitting) return;

    const passportIds = selected.map((r) => r.id);
    if (passportIds.length === 0) {
      showError({ message: t('참조메일을 발송할 참조인을 선택하세요.') });
      return;
    }
    if (message.length > MESSAGE_MAX_LENGTH) {
      showError({
        message: t('메시지는 {{count}}자까지 입력할 수 있습니다.', {
          count: MESSAGE_MAX_LENGTH,
        }),
      });
      return;
    }

    try {
      setSubmitting(true);
      // comment 필드: 메시지 평문 전달(선택). 빈 값이어도 참조인 추가/메일 발송돼야 함(백엔드).
      await addApprovalReferrers(documentId, passportIds, message);
      showSuccess({ message: t('참조인 설정과 참조메일이 발송되었습니다.') });
      await onAdded?.();
      onClose();
    } catch (e) {
      showErrorMessage(e);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      ref={modalRef}
      title={t('참조인 추가')}
      size="small"
      handleClose={handleClose}
      isCloseButtonDisabled={submitting}
      footerContent={
        <Stack direction="row" justifyContent="flex-end" sx={{ width: '100%' }}>
          <SendButton
            type="button"
            onClick={handleSubmit}
            disabled={submitting}
          >
            <span className="btn__label">{t('참조인 추가')}</span>
          </SendButton>
        </Stack>
      }
    >
      <Stack gap="20px" sx={{ width: '100%' }}>
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          {t('선택한 참조인에게 이 문서의 조회권한을 부여합니다.')}
        </Typography>

        <Stack gap="8px">
          <Label isRequired title={t('참조인')} size={14} />
          <ReferrerField
            selected={selected}
            onChange={(next) => {
              setSelected(next);
              setKeyword('');
            }}
            keyword={keyword}
            onKeywordChange={setKeyword}
            open={comboOpen}
            onOpenChange={setComboOpen}
            useUserSearch={useUserSearch}
            placeholder={t('이 문서를 공유할 임직원 혹은 부서를 검색해주세요')}
            hideHeader
            hideFooter
            disabled={submitting}
          />
        </Stack>

        <Stack gap="8px">
          <Label isRequired={false} title={t('Message')} size={14} />
          <MessageEditor value={message} onChange={setMessage} />
        </Stack>
      </Stack>
    </Modal>
  );
};

export default AddReferrerModal;
